import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Check, X, ChevronRight, ChevronLeft, Calendar, Trash2, Users } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { API_URL, fetchWithAuth, getImageUrl } from '../../api';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const MyEvents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Delete States
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Request Pagination States
  const [currentRequestIndex, setCurrentRequestIndex] = useState(0);

  // Pagination for Events
  const EVENTS_PAGE_SIZE = 6;
  const [visibleCreatedCount, setVisibleCreatedCount] = useState(EVENTS_PAGE_SIZE);
  const [visibleCollaboratedCount, setVisibleCollaboratedCount] = useState(EVENTS_PAGE_SIZE);

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const eventsData = await fetchWithAuth(`${API_URL}/my-events`);
        
        const lightEvents = Array.isArray(eventsData) ? eventsData.map(e => ({
          id: e.id,
          name: e.name,
          date: e.date,
          image_url: e.image_url,
          creator_id: e.creator_id,
          creator: e.creator ? { id: e.creator.id, name: e.creator.name } : undefined,
          role: e.role || e.current_user_role || 'staff', 
          role_label: e.role_label || 'Staff' 
        })) : [];
        
        setEvents(lightEvents);
        // const requestsData = await fetchWithAuth(`${API_URL}/my-pending-requests`);
        // setRequests(requestsData); 
        
        setError('');
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // --- HANDLERS ---
  const handleApprove = async (eventId, userId) => { console.log("Aprobar", eventId, userId); };
  const handleReject = async (eventId, userId) => { console.log("Rechazar", eventId, userId); };
  
  const handleNextRequest = () => {
    if (currentRequestIndex < requests.length - 1) {
        setCurrentRequestIndex(prev => prev + 1);
    }
  };

  const handlePrevRequest = () => {
    if (currentRequestIndex > 0) {
        setCurrentRequestIndex(prev => prev - 1);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await fetchWithAuth(`${API_URL}/events/${deleteTarget.id}`, { method: 'DELETE' });
      setEvents(prev => prev.filter(e => e.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err?.message || 'Error eliminando evento');
    } finally {
      setDeleting(false);
    }
  };

  // Helper
  const getEventDetails = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return { name: 'Unknown Event', isCreator: false };
    return {
      name: event.name,
      isCreator: event.creator_id === user?.id,
      eventObj: event
    };
  };

  // --- FILTROS ---
  const createdEvents = events
                        .filter(event => event.creator_id === user?.id)
                        .sort((a, b) => new Date(b.date) - new Date(a.date));

  const collaboratedEvents = events
                              .filter(event => event.creator_id !== user?.id)
                              .sort((a, b) => new Date(b.date) - new Date(a.date));
  const hasQRRequests = requests.length > 0;

  // --- RENDER ---
  if (loading) return <div className="p-10 text-center text-gray-500">Cargando eventos...</div>;
  if (error) return <div className="p-10 text-center text-red-400">{error}</div>;

  return (
    <div className="w-full space-y-10 animate-fade-in pb-20">
      
      {/* 1. SECTION: QR REQUESTS */}
      {hasQRRequests && (user?.role === 'admin' || (requests.length > 0 && getEventDetails(requests[currentRequestIndex].event_id).isCreator)) && (
        <section>
           <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-medium text-white flex items-center gap-2">
                 <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse"/> Solicitudes Pendientes
              </h2>
              <span className="text-sm text-gray-500">{currentRequestIndex + 1} / {requests.length}</span>
           </div>
           
           <div className="relative bg-[#ffffff03] border border-white/5 rounded-3xl p-6 md:p-8">
              {/* Contenido del request (abreviado por brevedad, igual que antes) */}
              <div className="absolute top-1/2 left-2 md:left-4 -translate-y-1/2 z-10">
                <button onClick={handlePrevRequest} disabled={currentRequestIndex === 0} 
                    className="p-2 rounded-full bg-black/50 hover:bg-white/10 disabled:opacity-30 text-white transition"><ChevronLeft size={20} /></button>
              </div>
              <div className="absolute top-1/2 right-2 md:right-4 -translate-y-1/2 z-10">
                <button onClick={handleNextRequest} disabled={currentRequestIndex === requests.length - 1}
                    className="p-2 rounded-full bg-black/50 hover:bg-white/10 disabled:opacity-30 text-white transition"><ChevronRight size={20} /></button>
              </div>
              <div className="max-w-lg mx-auto text-center">
                 <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-gray-800 to-black border border-white/10 flex items-center justify-center text-2xl font-bold text-white mb-4">
                    {requests[currentRequestIndex].user.name.charAt(0).toUpperCase()}
                 </div>
                 <h3 className="text-xl font-medium text-white mb-1">{requests[currentRequestIndex].user.name}</h3>
                 <p className="text-gray-500 text-sm mb-6">@{requests[currentRequestIndex].user.username}</p>
                 <div className="bg-white/5 rounded-xl p-4 mb-6 text-left flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400"><Calendar size={18}/></div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase">Solicita acceso para</p>
                        <p className="text-white font-medium">{getEventDetails(requests[currentRequestIndex].event_id).name}</p>
                    </div>
                 </div>
                 <div className="flex gap-3">
                    <button onClick={() => handleReject(requests[currentRequestIndex].event_id, requests[currentRequestIndex].user_id)} className="flex-1 py-3 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 transition font-medium text-sm flex items-center justify-center gap-2"><X size={16}/> Rechazar</button>
                    <button onClick={() => handleApprove(requests[currentRequestIndex].event_id, requests[currentRequestIndex].user_id)} className="flex-1 py-3 rounded-xl bg-white text-black hover:bg-gray-200 transition font-medium text-sm flex items-center justify-center gap-2 shadow-lg shadow-white/10"><Check size={16}/> Aprobar</button>
                 </div>
              </div>
           </div>
        </section>
      )}

      {/* 2. SECTION: CREATED EVENTS */}
      {createdEvents.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-6">
             <h2 className="text-2xl font-medium text-white">Mis Eventos</h2>
             <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">CREADOR</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {createdEvents.slice(0, visibleCreatedCount).map(event => {
              // Lógica de fechas
              const isPast = new Date(event.date) < new Date();
              
              return (
                <div 
                  key={event.id}
                  onClick={() => navigate(`/event/${event.id}`)}
                  className="group relative bg-[#ffffff03] border border-white/5 rounded-2xl overflow-hidden hover:border-white/20 transition-all cursor-pointer"
                >
                  <div className="relative aspect-video bg-gray-900 overflow-hidden">
                      <img 
                          src={getImageUrl(event.image_url)} 
                          alt={event.name}
                          // APLICA GRAYSCALE SI YA PASÓ
                          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 
                            ${isPast ? 'grayscale opacity-70' : ''}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"/>
                      
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(event); }}
                        className="absolute top-2 right-2 p-2 rounded-full bg-black/50 text-white/70 hover:text-red-400 hover:bg-black transition opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 size={16} />
                      </button>
                  </div>

                  <div className="p-5">
                      <h3 className={`text-lg font-medium mb-1 truncate ${isPast ? 'text-gray-400' : 'text-white'}`}>
                          {event.name}
                      </h3>
                      <p className="text-sm text-gray-400 flex items-center gap-2">
                          <Calendar size={14}/> {format(parseISO(event.date), 'EEEE, d MMMM', { locale: es })}
                          {isPast && <span className="text-[10px] uppercase bg-white/10 px-2 rounded">Finalizado</span>}
                      </p>
                  </div>
                </div>
              );
            })}
          </div>

          {createdEvents.length > visibleCreatedCount && (
            <button onClick={() => setVisibleCreatedCount(c => c + EVENTS_PAGE_SIZE)} 
                className="mt-6 w-full py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition text-sm">
                Ver más eventos
            </button>
          )}
        </section>
      )}

      {/* 3. SECTION: COLLABORATED EVENTS */}
      {collaboratedEvents.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-6">
             <h2 className="text-2xl font-medium text-white">Colaboraciones</h2>
             <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">STAFF</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {collaboratedEvents.slice(0, visibleCollaboratedCount).map(event => {
                // Lógica de fechas
                const isPast = new Date(event.date) < new Date();
                
                return (
                 <div 
                   key={event.id}
                   onClick={() => navigate(`/event/${event.id}`)}
                   className="group relative bg-[#ffffff03] border border-white/5 rounded-2xl overflow-hidden hover:border-white/20 transition-all cursor-pointer"
                 >
                   <div className="relative aspect-video bg-gray-900 overflow-hidden">
                       <img 
                           src={getImageUrl(event.image_url)} 
                           alt={event.name}
                           // APLICA GRAYSCALE SI YA PASÓ (Se eliminó la clase grayscale por defecto)
                           className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 
                             ${isPast ? 'grayscale opacity-70' : ''}`}
                       />
                       <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"/>
                   </div>
                   <div className="p-5">
                       <h3 className={`text-lg font-medium mb-1 truncate ${isPast ? 'text-gray-400' : 'text-white'}`}>
                            {event.name}
                       </h3>
                       <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-400 flex items-center gap-2">
                                <Calendar size={14}/> {format(parseISO(event.date), 'd MMM', { locale: es })}
                                {isPast && <span className="text-[10px] uppercase bg-white/10 px-2 rounded">Finalizado</span>}
                            </p>
                            <span className="text-xs text-purple-400 font-medium bg-purple-500/10 px-2 py-1 rounded">
                                {event.role_label || 'Colaborador'}
                            </span>
                       </div>
                   </div>
                 </div>
                );
            })}
          </div>
          
           {collaboratedEvents.length > visibleCollaboratedCount && (
            <button onClick={() => setVisibleCollaboratedCount(c => c + EVENTS_PAGE_SIZE)} 
                className="mt-6 w-full py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition text-sm">
                Ver más colaboraciones
            </button>
          )}
        </section>
      )}
      
      {/* 4. EMPTY STATE */}
      {!loading && !events.length && (
         <div className="py-20 text-center border border-dashed border-white/10 rounded-3xl bg-[#ffffff02]">
            <div className="w-16 h-16 mx-auto mb-4 bg-white/5 rounded-full flex items-center justify-center text-white/20"><Calendar size={32}/></div>
            <h3 className="text-xl font-medium text-white mb-2">No tienes eventos</h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">Empieza creando tu propia juerga o únete al equipo de un amigo.</p>
            <button onClick={() => navigate('/create')} className="px-6 py-3 rounded-full bg-white text-black font-medium hover:scale-105 transition">Crear Evento</button>
         </div>
      )}
      
      {/* DELETE MODAL (igual que antes) */}
      {deleteTarget && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <h3 className="text-lg font-medium text-white mb-2">¿Eliminar evento?</h3>
              <p className="text-gray-400 text-sm mb-6">Estás a punto de borrar <span className="text-white font-medium">"{deleteTarget.name}"</span>. Esta acción no se puede deshacer.</p>
              {deleteError && <p className="text-red-400 text-xs mb-4">{deleteError}</p>}
              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="flex-1 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition text-sm">Cancelar</button>
                <button onClick={handleConfirmDelete} disabled={deleting} className="flex-1 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition text-sm font-medium">{deleting ? '...' : 'Eliminar'}</button>
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default MyEvents;